import React, { useMemo } from 'react';
import { useDashboard } from '../../context/DashboardContext';
import { useAuth } from '../../auth/AuthContext';
import { useUserRole } from '../../hooks/useUserRole';
import DevFilterBar from '../layout/DevFilterBar';
import MonthPicker from '../kuroco/MonthPicker';
import StoreBreakdown from '../kuroco/StoreBreakdown';
import { buildKurocoGoalsData } from '../../adapters/kurocoData';
import { fmt, EM } from '../kuroco/fmt';
import { formatFocusValue, nextFocusSlot } from '../../utils/kpiFocusCatalog';
import { useGoalsConfig } from '../../utils/goalsLocalStore';
import { useNavigateWithFilters } from '../../utils/dashboardNav';

function LoadingBlock({ label }) {
  return (
    <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>
      {label || '読み込み中…'}
    </div>
  );
}

export default function GoalsScreen() {
  const navigate = useNavigateWithFilters();
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const {
    kpis,
    kpiHistory,
    mlForecasts,
    dataAsOf,
    kpiBreakdown,
    loading,
    selectedMonth,
    months,
    setSelectedMonth,
    formatMonthDisplay,
    selectedStore,
    selectedStylist,
  } = useDashboard();
  const [goals] = useGoalsConfig(user?.uid, selectedStore, selectedStylist);
  // 📋 毎日の行動プラン moved to ホーム (ActionPlanCard, 2026-09-30)

  const D = useMemo(
    () =>
      buildKurocoGoalsData({
        kpis,
        selectedMonth,
        loading,
        goals,
        dataAsOf,
        kpiHistory,
        mlForecasts,
      }),
    [kpis, kpiHistory, mlForecasts, dataAsOf, selectedMonth, loading, goals]
  );

  const gt = D.goalTrack;
  const s = D.monthSnap;
  const yen = (v) => (v != null && v !== EM ? fmt('currency', v) : EM);
  // 注力 / 追加KGI can be any KPI — format by that KPI's unit, not a fixed % / ¥
  const fmtUnit = (unit) => (v) => (v != null ? formatFocusValue(unit, v) ?? EM : EM);
  const pctOf = (actual, target) =>
    actual != null && target != null && target > 0 ? Math.round((actual / target) * 100) : null;

  // Per slot: [今月実績, 今月目標, 期限の目標] — the view model keeps ①②③ in fixed fields
  const slotNumbers = {
    1: [s.focusActual, s.focusTarget, D.finalFocusTarget],
    2: [s.unitActual, s.unitTarget, D.finalUnitTarget],
    3: [s.thirdActual, s.thirdTarget, D.finalThirdTarget ?? s.thirdTarget],
  };
  // One row per 注力 the user set (1–3); each opens its own slot (not a fixed kpi1 / kpi2)
  const monthMetrics = D.focusSlots.map((f) => {
    const [actual, target, final] = slotNumbers[f.slot];
    const pct = pctOf(actual, target);
    const show = fmtUnit(f.unit);
    return {
      slot: f.slot,
      icon: f.icon,
      name: f.label,
      pct,
      tone: pct != null && pct >= 100 ? 'good' : 'warn',
      final: show(final),
      target: show(target),
      actual: show(actual),
    };
  });
  const addFocusSlot = nextFocusSlot(D.focusSlots.map((f) => f.slot));

  // 月次進捗 columns: 売上, then the 追加KGI and the 注力 that are set — no empty 未選択 column
  const roadCols = [
    { key: 'sales', label: '売上', act: (m) => m.salesActual, tgt: (m) => m.salesTarget, show: yen },
    ...(D.subKgi
      ? [{ key: 'subKgi', label: D.subKgi.label, act: (m) => m.subKgiActual, tgt: (m) => m.subKgiTarget, show: fmtUnit(D.subKgi.unit) }]
      : []),
    ...D.focusSlots.map((f) => {
      const field = { 1: ['focusActual', 'focusTarget'], 2: ['unitActual', 'unitTarget'], 3: ['thirdActual', 'thirdTarget'] }[f.slot];
      return { key: `f${f.slot}`, label: f.label, act: (m) => m[field[0]], tgt: (m) => m[field[1]], show: fmtUnit(f.unit) };
    }),
  ];
  const roadGrid = { gridTemplateColumns: `34px repeat(${roadCols.length}, minmax(0, 1fr)) 46px` };
  // Wider than a phone once 追加KGI + 3 注力 are set: the table then scrolls sideways instead of squeezing
  const roadMinWidth = 34 + 46 + roadCols.length * 64 + (roadCols.length + 1) * 4 + 16;

  // Plan progress comes from the plan period (start month + length, JST today) — one source
  const periodPct = gt.elapsedPct ?? 0;
  const daysToGoal = gt.daysToGoal ?? null;
  // A goal, once set, is shown for every month; only its period is pinned to the start month.
  // 目標未設定 appears only when no goal exists at all.
  const noGoal = gt.finalSales == null && s.salesTarget == null;
  const beforePlan = Boolean(D.planPeriod?.isBeforePlan);
  const startLabel = (() => {
    const m = /^(\d{4})-(\d{2})$/.exec(gt.startMonth || '');
    return m ? `${Number(m[1])}年${Number(m[2])}月` : null;
  })();

  const subKgiPct = D.subKgi ? pctOf(s.subKgiActual, s.subKgiTarget) : null;

  const salesPctDisplay = s.salesPct != null ? s.salesPct : EM;
  const salesPctWidth =
    typeof s.salesPct === 'number' ? Math.min(100, s.salesPct) : 0;
  const remainingTarget =
    s.salesTarget != null && s.salesActual != null
      ? Math.max(0, s.salesTarget - s.salesActual)
      : null;

  if (D.isLoading && !kpis) {
    return <LoadingBlock label="目標データを読み込み中…" />;
  }

  return (
    <div className="fade">
      {isAdmin && <DevFilterBar />}
      <div className="home-period-row">
        <div className="databanner">
          <span className="dot" />
          <span>昨日 {D.period.dataAsOf} までの実績</span>
        </div>
        <MonthPicker
          value={selectedMonth}
          onChange={setSelectedMonth}
          availableMonths={months}
          formatMonthDisplay={formatMonthDisplay}
          title="表示月を選択"
          className="home-month-picker"
        />
      </div>

      {noGoal && (
        <div className="goal-month" style={{ marginBottom: 12 }}>
          <div className="goal-month-head">
            <span className="goal-month-title">{s.label}の実績</span>
            <span className="goal-month-tag">目標未設定</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', padding: '4px 2px 8px' }}>
            目標がまだ設定されていません。売上 <b style={{ color: 'var(--ink)' }}>{yen(s.salesActual)}</b>
            <button type="button" className="set-group-edit" style={{ marginLeft: 8 }} onClick={() => navigate('/settings/goaledit')}>
              目標を設定
            </button>
          </div>
        </div>
      )}
      <div className="goal-hero" style={noGoal ? { display: 'none' } : undefined}>
        <div className="goal-hero-eyebrow">
          <span className="goal-hero-badge">{gt.phaseLabel}</span>
          <span
            className="goal-hero-eyebrow-t"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/settings/goaledit?edit=period')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate('/settings/goaledit?edit=period');
            }}
          >
            {gt.goalDateLabel ? `〜${gt.goalDateLabel}` : `${gt.planLength ?? 6}ヶ月プラン`}
          </span>
          <button type="button" className="set-group-edit" style={{ marginLeft: 'auto' }} onClick={() => navigate('/settings/goaledit')}>
            目標を編集
          </button>
        </div>
        <button
          type="button"
          className="goal-hero-goal"
          style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', padding: 0 }}
          onClick={() => navigate('/settings/goaledit?edit=goal')}
        >
          <div className="goal-hero-goal-k">最終ゴール · 単月売上</div>
          <div className="goal-hero-goal-v">
            {yen(gt.finalSales)}
            <span className="goal-hero-goal-suffix">/ 月</span>
          </div>
          {D.subKgi && (
            <div className="goal-hero-goal-sub">
              ＋ {D.subKgi.label} {D.subKgi.target}
            </div>
          )}
        </button>
        <div className="goal-hero-prog">
          <div className="goal-hero-prog-track">
            <div className="goal-hero-prog-fill" style={{ width: `${periodPct}%` }} />
          </div>
          <div className="goal-hero-prog-meta">
            <span>
              {gt.months[0]?.label ?? EM} スタート{beforePlan && startLabel ? `（${startLabel}〜）` : ''}
            </span>
            <span className="mid">{periodPct}% 経過</span>
            <span>{gt.months[gt.months.length - 1]?.label ?? EM} ゴール</span>
          </div>
        </div>

        <div className="goal-hero-month">
          <div className="ghmonth-head">
            <span className="ghmonth-k">今月（{s.label}）の売上</span>
            <span className="ghmonth-pct">
              <span className="ghmonth-pct-k">達成率</span>
              {salesPctDisplay}
              {typeof s.salesPct === 'number' && <small>%</small>}
            </span>
          </div>
          <div className="ghmonth-figs">
            <span className="ghmonth-act">{yen(s.salesActual)}</span>
            <span className="ghmonth-sep">/</span>
            <span className="ghmonth-tgt">目標 {yen(s.salesTarget)}</span>
          </div>
          <div className="ghmonth-track">
            <div className="ghmonth-fill" style={{ width: `${salesPctWidth}%` }} />
          </div>
          <StoreBreakdown breakdown={kpiBreakdown} />
          {D.subKgi && (
            <div className="ghmonth-sub">
              <span className="ghmonth-k">今月の{D.subKgi.label}</span>
              <span className="ghmonth-tgt">
                {fmtUnit(D.subKgi.unit)(s.subKgiActual)} / 目標 {D.subKgi.target}
                {subKgiPct != null ? `（${subKgiPct}%）` : ''}
              </span>
            </div>
          )}
        </div>

        <div className="goal-hero-stats">
          <div className="ghstat ghstat--count">
            <div className="ghstat-k">ゴールまで</div>
            <div className="ghstat-v hl">
              {daysToGoal != null ? (
                <>
                  残り{daysToGoal}
                  <small>日</small>
                </>
              ) : (
                EM
              )}
            </div>
            <div className="ghstat-sub">{gt.goalDateLabel || EM}</div>
          </div>
          <div className="ghstat">
            <div className="ghstat-k">今月の残り目標</div>
            <div className="ghstat-v">{yen(remainingTarget)}</div>
            <div className={`ghstat-sub${s.paceTone ? ` pace-${s.paceTone}` : ''}`}>
              {gt.months[gt.currentIndex]?.label ?? EM}
              {s.paceLabel ? `・${s.paceLabel}` : ''}
            </div>
          </div>
        </div>
      </div>

      <div className="goal-month" style={noGoal ? { display: 'none' } : undefined}>
        <div className="goal-month-head">
          <span className="goal-month-title">今月（{s.label}）の注力指標</span>
          <span className="goal-month-tag">
            <i className="goal-month-dot" />
            ホームと連動
          </span>
        </div>
        {monthMetrics.map((m) => (
          <button
            type="button"
            className={`mmetric mmetric--${m.tone}`}
            key={m.slot}
            style={{ width: '100%', border: 'none', textAlign: 'left', cursor: 'pointer' }}
            onClick={() => navigate(`/settings/goaledit?edit=kpi${m.slot}`)}
          >
            <div className="mmetric-top">
              <span className="mmetric-ic">{m.icon}</span>
              <span className="mmetric-name">{m.name}</span>
              <span className="mmetric-pct">
                <span className="mmetric-pct-k">達成率</span>
                {m.pct != null ? m.pct : EM}
                {m.pct != null && <small>%</small>}
              </span>
            </div>
            <div className="mmetric-track">
              <div
                className="mmetric-fill"
                style={{ width: `${m.pct != null ? Math.min(100, m.pct) : 0}%` }}
              />
            </div>
            <div className="mmetric-figs">
              <div className="mmfig">
                <span className="mmfig-k">目標</span>
                <span className="mmfig-v">{m.target}</span>
              </div>
              <div className="mmfig">
                <span className="mmfig-k">実績</span>
                <span className="mmfig-v accent">{m.actual}</span>
              </div>
              <div className="mmfig">
                <span className="mmfig-k">{gt.planLength ?? 6}ヶ月後</span>
                <span className="mmfig-v">{m.final}</span>
              </div>
            </div>
          </button>
        ))}
        {addFocusSlot && (
          <button
            type="button"
            className="mmetric mmetric--add"
            onClick={() => navigate(`/settings/goaledit?edit=kpi${addFocusSlot}`)}
          >
            ＋ 注力指標を追加（{monthMetrics.length === 0 ? '1〜3つ' : `あと${3 - monthMetrics.length}つまで`}）
          </button>
        )}
      </div>

      <div className="roadmap" style={noGoal ? { display: 'none' } : undefined}>
        <div className="roadmap-title">
          月次進捗 <span className="rm-cap">実績 / 目標進捗</span>
        </div>
        <div className="gtable" style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: roadMinWidth }}>
            <div className="gt-head" style={roadGrid}>
              <span>月</span>
              {roadCols.map((c) => (
                <span key={c.key}>{c.label}</span>
              ))}
              <span>状態</span>
            </div>
            {gt.months.map((m, i) => {
              const cls =
                m.status === 'current'
                  ? 'current'
                  : m.status === 'final'
                    ? 'final'
                    : m.status === 'past'
                      ? 'past'
                      : 'upcoming';
              return (
                <div className={`gt-row ${cls}`} key={m.monthKey || i} style={roadGrid}>
                  <span className="gt-m">{m.label}</span>
                  {roadCols.map((c) => (
                    <span className="gt-metric" key={c.key}>
                      <span className={`gt-act${c.act(m) == null ? ' empty' : ''}`}>
                        {c.act(m) == null ? EM : c.show(c.act(m))}
                      </span>
                      <span className="gt-tgt">{c.show(c.tgt(m))}</span>
                    </span>
                  ))}
                  <span className={`gt-state gt-state--${cls}`}>{m.state}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="gt-foot">
          終了した月と当月の実績を表示。来店周期2〜3ヶ月のため、今月の行動は翌月以降の数値に反映されます。
        </div>
      </div>
    </div>
  );
}
